import { t } from 'i18next'
import { useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { TransformedToken } from 'style-dictionary'
import { cloneDeep, get, set } from 'es-toolkit/compat'
import { Button, Input, Modal, ModalContent, ModalFooter, ModalProps, ModalTitle, isHexColor } from '@dashboard/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { transformColorModifiers } from '@tokens-studio/sd-transforms'

import { WebsiteRead } from '@/api/dashboard'
import { websiteLastQueryOpts } from '@/layouts/websites'
import { FieldGroup, ToastSeverity, useToast } from '@/ui'
import { urlRegexp, useGenerateCompanyPath } from '@/libs'
import { themeQueryOpts } from '../api'
import { useTransformedTheme } from '../hooks'
import {
  FormModifyColorControl,
  FormTypeAssetControl,
  FormTypeBorderRadiusControl,
  FormTypeBorderWidthControl,
  FormTypeColorControl,
  FormTypeFontFamilyControl,
  FormTypeShadowControl,
} from './TokenStudioTokenEditForm'
import { ColorSwatch } from './ColorSwatch'

export interface TokenStudioTokenEditModalProps extends ModalProps {
  token: TransformedToken
}

export const TokenStudioTokenEditModal = ({ token: _token, ...rest }: TokenStudioTokenEditModalProps) => {
  const ql = useQueryClient()
  const { companyId, gameId } = useGenerateCompanyPath()
  const { data: website } = useQuery(websiteLastQueryOpts(companyId, gameId))
  const { data: transformedTheme, transform } = useTransformedTheme(gameId, website as WebsiteRead)
  const showToast = useToast()

  const { ...methods } = useForm({
    values: {
      token: _token,
    },
  })

  useEffect(() => {
    if (methods.formState.errors.root && methods.formState.errors.root.message) {
      showToast({ message: methods.formState.errors.root.message, severity: ToastSeverity.error })
    }
  }, [methods.formState.errors])

  const [token] = useWatch({
    control: methods.control,
    name: ['token'],
  })

  const previewModifyValue = useMemo(() => {
    try {
      if (token.original.value && token.original.$extensions?.['studio.tokens']?.modify) {
        // Get the token value - if it references another token, get the referenced token's value, otherwise use the current token's value
        const value = token.original.value.startsWith('{')
          ? get(transformedTheme?.jsonFlat, token.original.value.replaceAll('.', '-').replace(/[{}]/g, ''))
          : token.original.value
        return transformColorModifiers({ ...token.original, value }, { format: 'hex' })
      }
      return null
    } catch (err) {
      return null
    }
  }, [transformedTheme, token])

  const onSubmit = methods.handleSubmit(async data => {
    try {
      const updatedToken = data.token.original

      if (updatedToken.type === 'color') {
        if (!updatedToken.value || updatedToken.value === '#') {
          throw new Error('Invalid color value. Please enter a valid color.')
        }

        if (updatedToken.value && updatedToken.value.startsWith('#') && !isHexColor(updatedToken.value)) {
          throw new Error('Invalid color value. Please enter a valid color.')
        }
      }

      if (updatedToken.type === 'shadow') {
        updatedToken.value = {
          x: updatedToken.value.offsetX,
          y: updatedToken.value.offsetY,
          blur: updatedToken.value.blur,
          spread: updatedToken.value.spread,
          color: updatedToken.value.color,
          type: updatedToken.value.type,
        }
      }

      if (updatedToken.type === 'asset') {
        if (updatedToken.value) {
          if (!urlRegexp.test(updatedToken.value) && updatedToken.value !== ':empty') {
            throw new Error('Invalid URL format. Please enter a valid URL.')
          }
        } else {
          updatedToken.value = ':empty'
        }
      }

      if (updatedToken.type === 'fontFamily') {
        delete updatedToken.$extensions
        delete updatedToken.urls
      }

      ql.setQueryData(themeQueryOpts(gameId, website as WebsiteRead).queryKey, old => {
        if (!old) {
          return old
        }
        const copyOld = cloneDeep(old)
        set(copyOld, `theme.${_token.path.join('.')}`, updatedToken)
        return copyOld
      })

      await transform()
      rest?.onClose?.()
    } catch (err) {
      console.error(err)
      methods.setError('root', { message: err instanceof Error ? err.message : 'Unknown error' })
    }
  })

  return (
    <Modal {...rest}>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <ModalContent>
            <ModalTitle subtitle={_token.path.join('.')} onClose={rest.onClose}>
              Edit token
            </ModalTitle>
            <FieldGroup
              label={
                <span className="text-caption-sm font-normal text-text-tertiary">{t('theme-studio.forms.name')}</span>
              }
            >
              <Input disabled={true} value={token.name} />
            </FieldGroup>

            <div className="flex flex-col gap-4">
              {token.type === 'color' && (
                <>
                  <Controller
                    control={methods.control}
                    name="token.original.value"
                    render={({ field }) => <FormTypeColorControl {...field} />}
                  />
                  <Controller
                    control={methods.control}
                    name="token.original.$extensions"
                    render={({ field }) => <FormModifyColorControl {...field} />}
                  />
                </>
              )}

              {token.type === 'shadow' && (
                <>
                  <Controller
                    control={methods.control}
                    name="token.original.value.color"
                    render={({ field }) => <FormTypeColorControl {...field} />}
                  />
                  <Controller
                    control={methods.control}
                    name="token.original.value"
                    render={({ field }) => <FormTypeShadowControl {...field} />}
                  />
                </>
              )}

              {token.type === 'dimension' && token.attributes?.category === 'border-width' && (
                <Controller
                  control={methods.control}
                  name="token.original.value"
                  render={({ field }) => <FormTypeBorderWidthControl {...field} />}
                />
              )}

              {token.type === 'dimension' && token.attributes?.category === 'border-radius' && (
                <Controller
                  control={methods.control}
                  name="token.original.value"
                  render={({ field }) => <FormTypeBorderRadiusControl {...field} />}
                />
              )}

              {token.type === 'asset' && (
                <Controller
                  control={methods.control}
                  name="token.original.value"
                  render={({ field }) => <FormTypeAssetControl {...field} />}
                />
              )}

              {token.type === 'fontFamily' && (
                <Controller
                  control={methods.control}
                  name="token.original.value"
                  render={({ field }) => <FormTypeFontFamilyControl {...field} />}
                />
              )}

              {previewModifyValue && (
                <div className="flex items-center gap-2">
                  <ColorSwatch color={previewModifyValue} />
                  {previewModifyValue}
                </div>
              )}
            </div>
          </ModalContent>

          <ModalFooter>
            <Button type="button" size="sm" onClick={rest.onClose} color="secondary" variant="outline">
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              size="sm"
              loading={methods.formState.isSubmitting || methods.formState.isSubmitSuccessful}
            >
              {t('Save')}
            </Button>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
